import { useEffect, useMemo, useRef, useState } from 'react';
import Switch from 'react-switch';
import { COLLECTION_TAB_ALL, getBaseCard, getCardVariant, getCollectionCards, getCollectionTabs, humanCardName, VARIANT_MESSAGES } from '../utils/cards';
import { getDeck, KEYS } from '../utils/storage';
import CardViewer from './CardViewer';
import CardVariantOverlay from '../CardVariantOverlay';

export function CollectionModal({ isOpen, onClose, onReturnToStats, onOpenRelatedDecks, refreshTick = 0, showVariantCards, onShowVariantCardsChange, giftsEnabled, cardImageFolder = 'smallcards' }) {
  const [selectedCard, setSelectedCard] = useState(null);
  const [deck, setDeck] = useState(() => getDeck());
  const [activeTab, setActiveTab] = useState(COLLECTION_TAB_ALL);
  const modalContentRef = useRef(null);

  const { unique, total } = useMemo(() => {
    const uniqueCount = Object.keys(deck).length;
    const totalCount = Object.values(deck).reduce((a, b) => a + b, 0);
    return { unique: uniqueCount, total: totalCount };
  }, [deck]);

  const closeViewer = () => setSelectedCard(null);

  useEffect(() => {
    if (isOpen) {
      setDeck(getDeck());
      setActiveTab(COLLECTION_TAB_ALL);
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    } else {
      setSelectedCard(null);
    }
  }, [isOpen]);

  // Switching tabs while scrolled down would otherwise leave the player part-way down the new grid.
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleGiftCreated = () => {
    setDeck(getDeck());
  };

  useEffect(() => {
    if (isOpen) {
      setDeck(getDeck());
    }
  }, [refreshTick, isOpen]);

  const tabs = useMemo(() => getCollectionTabs(deck), [deck]);
  const visibleCards = useMemo(() => getCollectionCards(deck, activeTab), [deck, activeTab]);

  // Gifting away the last card of a retired variant removes its tab, so don't strand the player on it.
  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(COLLECTION_TAB_ALL);
    }
  }, [tabs, activeTab]);

  return (
    <div className={`modal winnings-modal ${isOpen ? 'open' : 'closed'}`}>
      <div className='modal-overlay' onClick={onClose}></div>
      <div className='modal-content' ref={modalContentRef}>
        <h2>Your Collection</h2>
        <p>
          <small>
            You've won <strong>
              {unique} unique card{unique !== 1 ? 's' : ''}
              <span style={{ display: 'inline-block' }}>{total > unique ? ` + ${total - unique} duplicates` : ''}</span>
            </strong> by beating Poqueta's daily puzzles!
          </small>
        </p>
        <p>
          <small>
            That's <strong>{total}</strong> card{total !== 1 ? 's' : ''} in total, including duplicates.
          </small>
        </p>


        <div className='example'>
          <label className='switch' style={{ margin: '0.5em' }}>
            <Switch
              onChange={(checked) => {
                onShowVariantCardsChange(checked);
                localStorage.setItem(KEYS.SHOW_VARIANT_CARDS, JSON.stringify(checked));
              }}
              onColor='#52141f'
              checked={showVariantCards}
              height={18}
              width={36}
              handleDiameter={18}
            />
            <span>Display special cards in puzzles</span>
          </label>
        </div>

        <div
          className='deck-display'
          id='collection-grid'
        >
          <div className='collection-filter'>
            <label htmlFor='collection-filter-select' className='collection-filter-label'>
              Filter cards
            </label>
            <select
              id='collection-filter-select'
              className={`collection-select ${tabs.length > 1 ? '' : 'hidden'} ${activeTab === COLLECTION_TAB_ALL ? 'all' : 'not-all'}`}
              aria-label='Filter collection by card type'
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              {tabs.map((tab) => (
                <option
                  key={`collection_tab_${tab.id}`}
                  value={tab.id}
                  title={VARIANT_MESSAGES[tab.id]}
                >
                  {tab.label} ({tab.count})
                </option>
              ))}
            </select>
          </div>

          {visibleCards.map((card) => {
            const hasCard = Boolean(deck[card]);
            const imageCard = hasCard ? getBaseCard(card).toLowerCase() : 'back1';
            // Unowned slots keep their variant treatment, so an empty slot in a variant tab
            // shows that variant's own card back rather than the plain standard one.
            const variant = getCardVariant(card);

            return (
              <div
                className='collected-card'
                key={`collection_${card}`}
                role={hasCard ? 'button' : undefined}
                tabIndex={hasCard ? 0 : -1}
                onClick={hasCard ? () => setSelectedCard(card) : undefined}
                onKeyDown={hasCard ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedCard(card);
                  }
                } : undefined}
              >
                <div className={`card ${variant ?? ''}`}>
                  {variant ? (
                    <CardVariantOverlay
                      variant={variant}
                      imageSrc={`./assets/${cardImageFolder}/${imageCard}.png`}
                      seed={card}
                      faceDown={!hasCard}
                      alt={card}
                      title={humanCardName(card)}
                      className='front'
                      animated={true}
                    />
                  ) : (
                    <img
                      alt={card}
                      title={humanCardName(card)}
                      className='front'
                      loading='eager'
                      decoding='sync'
                      src={`./assets/${cardImageFolder}/${imageCard}.png`}
                    />
                  )}
                </div>
                {hasCard ? <span className='count'>x{Math.min(deck[card], 999)}</span> : ''}
              </div>
            );
          })}
        </div>

        <div className='modal-buttons'>
          <button onClick={onReturnToStats}>Return to Stats</button>
        </div>
      </div>

      <CardViewer
        isOpen={Boolean(selectedCard)}
        card={selectedCard}
        cardImageFolder={cardImageFolder}
        onClose={closeViewer}
        onGiftCreated={handleGiftCreated}
        onOpenRelatedDecks={onOpenRelatedDecks}
        giftsEnabled={giftsEnabled}
      />
    </div>
  );
}
